// New-mail notifications on this device (docs/WEBMAIL.md, phase 5): subscribing with the browser's push service and
// registering the subscription with the server (/api/push/devices), which pushes to it when new mail arrives in the
// folders chosen for notifications (push_folders in /api/settings, the same on every device). Whether notifications
// come is decided per device. The service worker (sw.ts) shows them.

import { ApiError, api, type AppSettings, type PushDevice } from "./api";
import { deviceId, deviceName, fromBase64Url, takeTap } from "./notify";

/** "install-first": on the iPhone and iPad only the app on the home screen gets notifications (iOS 16.4 and later). */
export type PushSupport = "supported" | "install-first" | "unsupported";

/** localStorage: the id this device was registered under, to notice when the browser replaces or drops it. */
const DEVICE_KEY = "push-device";

/** The user refused, or the browser did, when asked to show notifications. */
export class PushRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PushRefused";
  }
}

export function pushSupport(): PushSupport {
  if (typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window) return "supported";
  const apple = typeof navigator !== "undefined" && (/iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));
  return apple && !standalone() ? "install-first" : "unsupported";
}

function standalone(): boolean {
  return matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** The folders new mail is notified in (Inbox until chosen). */
export function pushFolders(settings: AppSettings | null): string[] {
  return settings?.push_folders ?? ["Inbox"];
}

/** The folders notified, with new mail in `folder` notified or not. */
export function notifyFolder(chosen: string[], folder: string, notify: boolean): string[] {
  const others = chosen.filter((f) => f.toLowerCase() !== folder.toLowerCase());
  return notify ? [...others, folder] : others;
}

/** This device's push subscription and its id on the server (whether the server knows it or not); null without one. */
export async function thisDevice(): Promise<{ subscription: PushSubscription; id: string } | null> {
  if (pushSupport() !== "supported") return null;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription ? { subscription, id: await deviceId(subscription.endpoint) } : null;
}

/** Turns notifications on for this device; called from a tap, as the browser asks for permission. */
export async function turnOn(publicKey: string): Promise<PushDevice> {
  // First of all, while it still counts as an answer to the tap: Safari asks only then.
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new PushRefused(permission === "denied" ? "Notifications are blocked for this app." : "Notifications were not allowed.");
  }
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !sameKey(subscription, publicKey)) {
    await subscription.unsubscribe(); // made for another server key: pushes signed with ours would be refused
    subscription = null;
  }
  try {
    subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromBase64Url(publicKey) });
  } catch (e) {
    const hint = refusalHint(navigator.userAgent, "brave" in navigator);
    throw new PushRefused(`The browser's push service refused: ${e instanceof Error ? e.message : String(e)}.${hint ? ` ${hint}` : ""}`);
  }
  return register(subscription);
}

/** What to try when subscribing fails: Brave on the desktop refuses (an AbortError) unless "Use Google services for
 * push messaging" is on. Brave on Android has no such setting and needs none (tried on 16 October 2026). */
export function refusalHint(userAgent: string, brave: boolean): string {
  return brave && !/Android/.test(userAgent) ? "In Brave, turn on “Use Google services for push messaging” in the privacy settings and try again." : "";
}

async function register(subscription: PushSubscription): Promise<PushDevice> {
  const device = await api.addPushDevice(subscription.toJSON(), deviceName(navigator.userAgent, "brave" in navigator, navigator.maxTouchPoints));
  remember(device.id);
  return device;
}

/** Turns notifications off for this device: the subscription ends and the server forgets the device. */
export async function turnOff(): Promise<void> {
  const here = await thisDevice();
  const id = here?.id ?? remembered();
  await here?.subscription.unsubscribe();
  if (id) await forget(id);
  remember(null);
}

/** On start: when the browser replaced this device's subscription (it may) or dropped it (notifications were blocked
 * since), the server is told. Nothing happens while it is the one registered, so a device removed from the list on
 * another device stays removed until it is turned on again here. */
export async function keepRegistered(): Promise<void> {
  const known = remembered();
  if (!known || pushSupport() !== "supported") return;
  const here = await thisDevice();
  if (here?.id === known) return;
  if (here && Notification.permission === "granted") await register(here.subscription);
  else remember(null);
  await forget(known);
}

async function forget(id: string): Promise<void> {
  try {
    await api.removePushDevice(id);
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 404)) throw e;
  }
}

function sameKey(subscription: PushSubscription, publicKey: string): boolean {
  const key = subscription.options?.applicationServerKey;
  if (!key) return true; // not told: keep it
  const have = new Uint8Array(key);
  const want = fromBase64Url(publicKey);
  return have.length === want.length && have.every((b, i) => b === want[i]);
}

/** When, after the app starts or comes to the front, it looks for a tap the service worker kept: the tap can reach
 * the worker after the app is in front already. */
const TAP_LOOKS_MS = [0, 300, 1000, 2500, 5000];

/** Follows a tapped notification that the service worker's message did not bring (notify.ts takeTap): looked for
 * when the app starts and each time it comes to the front. Returns the function that stops it. */
export function followTaps(onTap: (url: string) => void): () => void {
  let timers: ReturnType<typeof setTimeout>[] = [];
  const stop = () => {
    timers.forEach(clearTimeout);
    timers = [];
  };
  const look = () => {
    stop();
    timers = TAP_LOOKS_MS.map((ms) =>
      setTimeout(() => {
        takeTap().then(
          (url) => {
            if (!url) return;
            stop();
            onTap(url);
          },
          () => {},
        );
      }, ms),
    );
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") look();
  };
  look();
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    stop();
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/** Closes the notification of a message that has been opened. */
export async function closeNotification(messageId: string): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    for (const n of (await registration?.getNotifications({ tag: messageId })) ?? []) n.close();
  } catch {
    // not supported here: it stays until it is tapped or swiped away
  }
}

function remembered(): string | null {
  try {
    return localStorage.getItem(DEVICE_KEY);
  } catch {
    return null;
  }
}

function remember(id: string | null): void {
  try {
    if (id) localStorage.setItem(DEVICE_KEY, id);
    else localStorage.removeItem(DEVICE_KEY);
  } catch {
    // not kept: a replaced subscription is noticed on the Notifications screen instead
  }
}
